import { DI_TOKENS } from '@src/di/tokens';
import { uploadCredentialsService } from '@src/services/UploadCredentialsService';
import { LogManager } from '@src/utility/Logger';
import { inject, injectable } from 'tsyringe';

import { performMultipartUpload } from './MultipartCore';

import type { UploadWorkerProvider } from './UploadWorkerProvider';
import type { S3UploadWorkerOutboundMessage } from '@src/workers/s3UploadMessages';

// Baseline concurrency; may tune later (kept constant for now)
const UPLOAD_THREADS = 5;

export interface UploadOptions {
  onUploadProgress?: (progress: number, done: boolean, total?: number) => void;
  preferAwsSdk?: boolean;
  authToken?: string;
  offloadToWorker?: boolean;
  /**
   * Milliseconds to wait for the worker to acknowledge (via a 'started' message) after posting
   * the start message. If the timeout elapses, the upload falls back to the main‑thread path.
   * Defaults to 4000ms.
   */
  workerStartupTimeoutMs?: number;
  /**
   * Called once when the upload mechanism has been definitively chosen.
   * Values:
   *  - 'worker' when worker-based multipart took over.
   *  - 'main-thread' when falling back to main thread AWS SDK multipart.
   */
  onResolvedMechanism?: (mechanism: 'worker' | 'main-thread') => void;
}

// Logger
const uploadLogger = LogManager.getLogger('upload:multipart');

function computePartSizeBytes(fileSize: number): number {
  const MB = 1024 * 1024;
  const MAX_PARTS = 10000;
  if (fileSize <= 128 * MB) return 8 * MB;
  if (fileSize <= 512 * MB) return 12 * MB;
  if (fileSize <= 2 * 1024 * MB) return 16 * MB;
  if (fileSize <= 5 * 1024 * MB) return 32 * MB;
  const minPartSize = Math.ceil(fileSize / MAX_PARTS);
  const targetParts = 1500;
  const derived = Math.max(Math.ceil(fileSize / targetParts), minPartSize);
  if (minPartSize > 128 * MB) return minPartSize;
  const clamped = Math.min(Math.max(derived, 32 * MB, minPartSize), 128 * MB);
  return clamped;
}

/** Test helper to expose part size computation logic
 * @internal
 */

export const __test_computePartSizeBytes = computePartSizeBytes;

export function getExternalUploadsBucketPathKey(
  fileKey: string,
  organization_id: string | null | undefined,
): string {
  return `externalUploads/${organization_id || 'no_org'}/${fileKey}`;
}

export function renameFile(file: File, newBasename: string): File {
  const fileExtension = file.name.split('.').slice(-1)[0];
  const newName = `${newBasename}.${fileExtension}`;
  return new File([file], newName, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

async function tryStartWorkerUpload(
  file: File,
  showcaseId: string | null | undefined,
  options: UploadOptions | undefined,
  resolve: () => void,
  workerProvider: UploadWorkerProvider,
): Promise<{ started: boolean; controller?: { abort: () => void } }> {
  if (!options?.preferAwsSdk) return { started: false };
  const wantWorker = options.offloadToWorker !== false;
  if (!wantWorker) return { started: false };
  if (!showcaseId) return { started: false };
  const worker = workerProvider.get();
  if (!worker) return { started: false };

  const credentialsResult = await uploadCredentialsService.fetch(
    showcaseId,
    options?.authToken,
  );
  if (!credentialsResult.ok) {
    // Fallback to main-thread path is possible; still classify as error for monitoring of credential retrieval issues.
    uploadLogger.error('worker-credentials-failed', {
      errorKind: credentialsResult.error.kind,
      message: credentialsResult.error.message,
      nonFatal: true,
      action: 'fallback-to-main-thread',
    });
    return { started: false };
  }

  const partSizeBytes = computePartSizeBytes(file.size);
  const partSizeMB = Math.round(partSizeBytes / (1024 * 1024));
  const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const key = getExternalUploadsBucketPathKey(file.name, showcaseId);
  const startupTimeoutMs = options?.workerStartupTimeoutMs ?? 4000;
  let startedAck = false;

  // Post start message immediately so worker can respond with 'started'.
  try {
    worker.postMessage({
      type: 'start',
      id,
      file,
      options: {
        bucket: credentialsResult.value.bucket,
        region: credentialsResult.value.region,
        key,
        credentials: credentialsResult.value.credentials,
        partSizeMB,
        queueSize: UPLOAD_THREADS,
      },
    });
  } catch (err) {
    // Handshake start message failed to post; will fallback to main-thread. Treat as error for visibility.
    uploadLogger.error('worker-start-post-failed', {
      message: String(err),
      nonFatal: true,
      action: 'fallback-to-main-thread',
    });
    return { started: false };
  }

  const handshakeOk: boolean = await new Promise(resolveHandshake => {
    const timer = setTimeout(() => {
      if (!startedAck) {
        // Timeout establishing worker handshake; fallback to main-thread path.
        uploadLogger.error('worker-start-timeout', {
          file: file.name,
          sizeBytes: file.size,
          showcaseId,
          timeoutMs: startupTimeoutMs,
          nonFatal: true,
          action: 'fallback-to-main-thread',
        });
        try {
          worker.postMessage({ type: 'abort', id });
        } catch {
          /* ignore */
        }
        resolveHandshake(false);
      }
    }, startupTimeoutMs);

    const listener = ({
      data,
    }: MessageEvent<S3UploadWorkerOutboundMessage>) => {
      if (!data || typeof data !== 'object' || !('type' in data)) return;
      if (data.type === 'started' && data.id === id) {
        startedAck = true;
        clearTimeout(timer);
        worker.removeEventListener('message', listener);
        resolveHandshake(true);
      } else if (
        (data.type === 'error' || data.type === 'aborted') &&
        data.id === id
      ) {
        clearTimeout(timer);
        worker.removeEventListener('message', listener);
        // Worker reported error/abort before start; fallback to main-thread path.
        uploadLogger.error('worker-start-failed-initial', {
          type: data.type,
          file: file.name,
          showcaseId,
          key,
          nonFatal: true,
          action: 'fallback-to-main-thread',
        });
        resolveHandshake(false);
      }
    };
    worker.addEventListener('message', listener);
  });

  if (!handshakeOk) return { started: false };

  // Mechanism resolved: worker path.
  try {
    options?.onResolvedMechanism?.('worker');
  } catch {
    /* ignore */
  }

  // Handshake succeeded; attach streaming listener.
  const onMessage = ({ data }: MessageEvent<S3UploadWorkerOutboundMessage>) => {
    if (!data || typeof data !== 'object' || !('type' in data)) return;
    if (typeof data.type !== 'string') return;
    if (data.type === 'progress') {
      options?.onUploadProgress?.(data.progress, false, data.total);
    } else if (data.type === 'complete') {
      options?.onUploadProgress?.(100, true, file.size);
      worker.removeEventListener('message', onMessage);
      resolve();
    } else if (data.type === 'error' || data.type === 'aborted') {
      worker.removeEventListener('message', onMessage);
      // Upload already in progress and failed inside worker – cannot fallback; critical error.
      uploadLogger.error('worker-upload-error', {
        type: data.type,
        message:
          data.type === 'aborted'
            ? 'Upload aborted by user'
            : // Narrow type: error message only exists for error variant
              data.type === 'error'
              ? data.message
              : 'unknown',
        fileName: file.name,
        sizeBytes: file.size,
        showcaseId,
        key,
      });
      // Fallback is not possible here because we've already begun uploading (partial upload may exist).
      // Propagate error so caller can decide; throwing inside event handler is noisy, so just log.
    }
  };
  worker.addEventListener('message', onMessage);
  const onError = (ev: ErrorEvent) => {
    uploadLogger.error('worker-error', {
      message: ev.message,
      file: file.name,
    });
  };
  worker.addEventListener('error', onError);
  try {
    worker.postMessage({
      type: 'start',
      id,
      file,
      options: {
        bucket: credentialsResult.value.bucket,
        region: credentialsResult.value.region,
        key,
        credentials: credentialsResult.value.credentials,
        partSizeMB,
        queueSize: UPLOAD_THREADS,
      },
    });
    return {
      started: true,
      controller: {
        abort: () => {
          try {
            worker.postMessage({ type: 'abort', id });
          } catch {
            /* ignore */
          }
        },
      },
    };
  } catch (err) {
    worker.removeEventListener('message', onMessage);
    worker.removeEventListener('error', onError);
    uploadLogger.error('worker-start-failed', {
      message: String(err),
      nonFatal: true,
      action: 'fallback-to-main-thread',
    });
    return { started: false };
  }
}

@injectable()
export class UploadService {
  constructor(
    @inject(DI_TOKENS.UPLOAD_WORKER_PROVIDER)
    private readonly workerProvider: UploadWorkerProvider,
  ) {}

  async uploadFileToS3ExternalUploads(
    file: File,
    showcaseId: string | null | undefined,
    options?: UploadOptions,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      (async () => {
        const workerAttempt = await tryStartWorkerUpload(
          file,
          showcaseId,
          options,
          resolve,
          this.workerProvider,
        );
        if (workerAttempt.started) return;
        if (!options?.preferAwsSdk) {
          resolve();
          return;
        }
        try {
          if (!showcaseId) {
            resolve();
            return;
          }
          const credentialsResult = await uploadCredentialsService.fetch(
            showcaseId,
            options?.authToken,
          );
          if (!credentialsResult.ok) {
            uploadLogger.error('main-thread-credentials-failed', {
              errorKind: credentialsResult.error.kind,
              nonFatal: true,
              action: 'resolve-and-continue-legacy-upstream',
            });
            // Legacy behavior: silently resolve (caller may proceed with legacy path upstream)
            resolve();
            return;
          }
          const partSizeBytes = computePartSizeBytes(file.size);
          const partSizeMB = Math.round(partSizeBytes / (1024 * 1024));
          const key = getExternalUploadsBucketPathKey(file.name, showcaseId);
          // Mechanism resolved (fallback main-thread) – invoke once if not already called by worker path.
          try {
            options?.onResolvedMechanism?.('main-thread');
          } catch {
            /* ignore */
          }

          const controller = performMultipartUpload({
            bucket: credentialsResult.value.bucket,
            region: credentialsResult.value.region,
            key,
            file,
            contentType: file.type,
            credentials: credentialsResult.value.credentials,
            queueSize: UPLOAD_THREADS,
            partSizeMB,
            onProgress: p => {
              options?.onUploadProgress?.(p.percent, false, p.total);
            },
          });
          controller.promise
            .then(() => {
              options?.onUploadProgress?.(100, true, file.size);
              resolve();
            })
            .catch(err => {
              uploadLogger.error('main-thread-upload-error', {
                message: String(err),
              });
              reject(err);
            });
        } catch (err) {
          uploadLogger.error('main-thread-upload-init-failed', {
            message: String(err),
          });
          if (err instanceof Error) reject(err);
          else reject(new Error(String(err)));
        }
      })();
    });
  }
}

// Public interface surface for DI typing (keep minimal and stable)
export interface IUploadService {
  uploadFileToS3ExternalUploads(
    file: File,
    showcaseId: string | null | undefined,
    options?: UploadOptions,
  ): Promise<void>;
}
// Class is structurally compatible; keep explicit interface for DI typing
